import { lazy, Suspense, useEffect, useState } from "react";
import { useHashRoute, navigate } from "./lib/useHashRoute";
import { useTheme } from "./lib/theme";
import { apiHealth, IS_STATIC_BUILD, readStats, type UsageStats } from "./lib/api";
import type { HealthStatus } from "../shared/types";
import { SearchHome } from "./components/SearchHome";
import { IconInfo, IconMoon, IconSearch, IconSun, IconTeiaMark } from "./components/Icons";
import { AccountMenu } from "./components/AccountMenu";
import { CompanyPage } from "./components/CompanyPage";
import { PersonPage } from "./components/PersonPage";
import { ConnectPage } from "./components/ConnectPage";
import { GuidePage } from "./components/GuidePage";
import { NotFoundPage } from "./components/NotFoundPage";

// O explorador carrega o Cytoscape, que sozinho pesa mais do que o resto do
// app inteiro. Quem entra pela busca ou pela ficha não precisa dele, então
// essas duas telas só baixam quando alguém abre.
const NetworkPage = lazy(() => import("./components/NetworkPage").then((m) => ({ default: m.NetworkPage })));
const StudioPage = lazy(() => import("./components/StudioPage").then((m) => ({ default: m.StudioPage })));
// Conta, pagamento, lote e a página de apresentação também só baixam quando alguém abre:
// quem só consulta CNPJ não paga o peso delas.
const LandingPage = lazy(() => import("./components/LandingPage").then((m) => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import("./components/auth/AuthPages").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import("./components/auth/AuthPages").then((m) => ({ default: m.SignupPage })));
const ForgotPage = lazy(() => import("./components/auth/AuthPages").then((m) => ({ default: m.ForgotPage })));
const ResetPage = lazy(() => import("./components/auth/AuthPages").then((m) => ({ default: m.ResetPage })));
const AccountPage = lazy(() => import("./components/conta/AccountPage").then((m) => ({ default: m.AccountPage })));
const LotePage = lazy(() => import("./components/LotePage").then((m) => ({ default: m.LotePage })));
const LegalPage = lazy(() => import("./components/LegalPage").then((m) => ({ default: m.LegalPage })));

/** Telas de entrar e criar conta: o cabeçalho fica limpo, sem busca. */
const AUTH_PAGES = new Set(["login", "signup", "forgot", "reset"]);

function PageLoading() {
  return (
    <div className="page-loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden />
      <span>Carregando…</span>
    </div>
  );
}

function HeaderSearch() {
  const [value, setValue] = useState("");
  return (
    <form
      className="header-search"
      onSubmit={(event) => {
        event.preventDefault();
        const term = value.trim();
        if (!term) return;
        navigate({ page: "home", mode: "auto", q: term });
        setValue("");
      }}
      role="search"
    >
      <IconSearch size={15} />
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Buscar…"
        aria-label="Buscar empresa, sócio ou CNPJ"
      />
    </form>
  );
}

export function App() {
  const route = useHashRoute();
  const [theme, toggleTheme] = useTheme();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [stats, setStats] = useState<UsageStats>({ total: 0, session: 0 });

  useEffect(() => {
    const controller = new AbortController();
    apiHealth(controller.signal).then(setHealth).catch(() => setHealth(null));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setStats(readStats());
    const onStats = () => setStats(readStats());
    window.addEventListener("teia:stats", onStats);
    return () => window.removeEventListener("teia:stats", onStats);
  }, [route.page]);

  const isNetwork = route.page === "network";

  // A rede usa o menu sólido de largura inteira, independente do modelo do Estúdio.
  useEffect(() => {
    document.documentElement.dataset.page = route.page;
  }, [route.page]);

  return (
    <div className="app">
      <header className="header">
        <div className="container header-inner">
          <button className="brand" onClick={() => navigate({ page: "home" })} aria-label="Teia, rede societária: ir para a busca">
            <span className="brand-mark" aria-hidden><IconTeiaMark size={19} /></span>
            <span className="brand-name">
              <span className="brand-word">Teia</span>
              <span className="brand-tag">Rede Societária</span>
            </span>
          </button>
          <nav className="header-nav">
            <button
              className={`header-link${route.page === "guide" ? " active" : ""}`}
              onClick={() => navigate({ page: "guide" })}
            >
              Como consultar
            </button>
            <button
              className={`header-link${route.page === "connect" ? " active" : ""}`}
              onClick={() => navigate({ page: "connect" })}
            >
              Conectar a IA
            </button>
            <button
              className={`header-link${route.page === "landing" ? " active" : ""}`}
              onClick={() => navigate({ page: "landing", anchor: "planos" })}
            >
              Planos
            </button>
          </nav>
          <div className="header-spacer" />
          {route.page !== "home" && route.page !== "studio" && route.page !== "landing" && !AUTH_PAGES.has(route.page) && <HeaderSearch />}
          <button
            className="icon-btn"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
            title={theme === "dark" ? "Tema claro" : "Tema escuro"}
          >
            {theme === "dark" ? <IconSun size={16} /> : <IconMoon size={15} />}
          </button>
          <AccountMenu page={route.page} />
        </div>
      </header>

      {IS_STATIC_BUILD && route.page === "home" && (
        <div className="container" style={{ paddingTop: 12 }}>
          <div className="demo-banner" style={{ marginBottom: 0 }}>
            <IconInfo size={16} /> <span>Versão estática no GitHub Pages: ficha e rede por CNPJ funcionam direto do navegador.
            Busca por nome e por sócio precisa do servidor. O README explica como rodar completo.</span>
          </div>
        </div>
      )}
      {route.page === "home" && (
        <SearchHome
          key={`${route.mode ?? ""}|${route.q ?? ""}`}
          initialMode={route.mode}
          initialQuery={route.q}
        />
      )}
      {route.page === "company" && <CompanyPage cnpj={route.cnpj} />}
      {route.page === "person" && <PersonPage key={`${route.name}|${route.doc ?? ""}|${route.de ?? ""}`} name={route.name} doc={route.doc} de={route.de} />}
      {route.page === "network" && (
        <Suspense fallback={<PageLoading />}>
          <NetworkPage cnpj={route.cnpj} theme={theme} />
        </Suspense>
      )}
      {route.page === "connect" && <ConnectPage />}
      {route.page === "guide" && <GuidePage />}
      {route.page === "notFound" && <NotFoundPage path={route.path} />}
      {route.page === "studio" && (
        <Suspense fallback={<PageLoading />}>
          <StudioPage theme={theme} />
        </Suspense>
      )}
      {route.page === "landing" && (
        <Suspense fallback={<PageLoading />}>
          <LandingPage key={route.anchor ?? "topo"} anchor={route.anchor} />
        </Suspense>
      )}
      {route.page === "login" && (
        <Suspense fallback={<PageLoading />}>
          <LoginPage volta={route.volta} />
        </Suspense>
      )}
      {route.page === "signup" && (
        <Suspense fallback={<PageLoading />}>
          <SignupPage plano={route.plano} ciclo={route.ciclo} volta={route.volta} />
        </Suspense>
      )}
      {route.page === "forgot" && (
        <Suspense fallback={<PageLoading />}>
          <ForgotPage />
        </Suspense>
      )}
      {route.page === "reset" && (
        <Suspense fallback={<PageLoading />}>
          <ResetPage token={route.token} />
        </Suspense>
      )}
      {route.page === "account" && (
        <Suspense fallback={<PageLoading />}>
          <AccountPage route={route} />
        </Suspense>
      )}
      {route.page === "batch" && (
        <Suspense fallback={<PageLoading />}>
          <LotePage />
        </Suspense>
      )}
      {route.page === "legal" && (
        <Suspense fallback={<PageLoading />}>
          <LegalPage key={route.doc} doc={route.doc} />
        </Suspense>
      )}

      {!isNetwork && (
        <footer className="footer">
          <div className="container">
            <div className="footer-top">
              <div className="footer-brand">
                <span className="brand-mark" aria-hidden><IconTeiaMark size={17} /></span>
                <span>
                  <span className="footer-brand-name brand-word">Teia</span>
                  <span className="footer-brand-tag">Quem está por trás do CNPJ</span>
                </span>
              </div>
              <nav className="footer-nav">
                <button className="footer-link-plain" onClick={() => navigate({ page: "guide" })}>Como consultar</button>
                <button className="footer-link-plain" onClick={() => navigate({ page: "connect" })}>Conectar a IA</button>
                <button className="footer-link-plain" onClick={() => navigate({ page: "landing" })}>Conheça o Teia</button>
                <button className="footer-link-plain" onClick={() => navigate({ page: "landing", anchor: "planos" })}>Planos</button>
                <button className="footer-link-plain" onClick={() => navigate({ page: "legal", doc: "termos" })}>Termos</button>
                <button className="footer-link-plain" onClick={() => navigate({ page: "legal", doc: "privacidade" })}>Privacidade</button>
              </nav>
              <div className="footer-status">
                {health ? (
                  (() => {
                    const online = health.providers.filter((provider) => provider.ok).length;
                    const total = health.providers.length;
                    const detail = health.providers
                      .map((provider) => `${provider.name}: ${provider.ok ? "no ar" : provider.note ?? "sem resposta"}`)
                      .join("\n");
                    return (
                      <span
                        className={`footer-server ${online === total ? "ok" : online > 0 ? "warn" : "down"}`}
                        title={`${detail}\nVerificado em ${new Date(health.checkedAt).toLocaleTimeString("pt-BR")}`}
                      >
                        <span className="footer-server-dot" aria-hidden />
                        Servidor Online
                        <span className="footer-server-count mono">{online}/{total}</span>
                        Fontes de pesquisa
                      </span>
                    );
                  })()
                ) : (
                  <span className="footer-server checking">
                    <span className="footer-server-dot" aria-hidden />
                    Verificando as fontes…
                  </span>
                )}
              </div>
            </div>
            <div className="footer-bottom">
              <p className="footer-note">
                © {new Date().getFullYear()} Teia · Dados públicos da Receita Federal, reunidos para
                uso informativo, atualizados diariamente.
                <span className="footer-powered">Powered by Nexus</span>
              </p>
              <p className="footer-note footer-count">
                Você realizou um total de: <strong>{stats.total} consulta{stats.total === 1 ? "" : "s"}</strong>
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
